import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFavorites } from '@/hooks/useFavorites';
import ReviewList from '@/pages/detail/components/ReviewList';
import RelatedMovies from '@/pages/detail/components/RelatedMovies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useDocumentMeta({
    title: movie ? `${movie.title} - 神马影院影视详情` : '影视详情 - 神马影院',
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）是由${movie.director}执导，${movie.actors.join('、')}主演的${movie.genres.join('、')}作品。${movie.synopsis.slice(0, 70)}…`
      : '神马影院影视详情页。',
    keywords: movie ? `${movie.title},${movie.genres.join(',')},${movie.actors.join(',')},神马影院` : '神马影院,影视详情',
    path: `/detail/${id}`,
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  if (!movie) {
    return (
      <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-center px-4 py-28 text-center md:px-6">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-4xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这部影视</h1>
        <p className="mt-2 text-sm text-foreground-500">该内容可能已下架或链接有误，回到首页看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="mt-6 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-500"
        >
          <i className="ri-home-4-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: `${movie.title} - 神马影院`, text: movie.synopsis, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('分享已取消，可手动复制地址栏链接');
    }
  };

  const handleFavorite = () => {
    const added = toggleFavorite(movie.id);
    showToast(added ? '已加入收藏（仅保存在本地浏览器）' : '已从收藏中移除');
  };

  return (
    <div>
      {toast && (
        <div className="fixed left-1/2 top-20 z-50 -translate-x-1/2 rounded-full bg-primary-950 px-5 py-2.5 text-sm text-background-50 animate-fade-in">
          {toast}
        </div>
      )}

      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop || movie.cover}
            alt={`${movie.title}剧照`}
            title={`${movie.title} - 神马影院影视资源`}
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
          <nav className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-background-300" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${movie.type}`} className="transition-colors hover:text-accent-400">
              {movie.type === 'movie' ? '电影' : '电视剧'}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-medium text-background-100">{movie.title}</span>
          </nav>

          <div className="flex flex-col gap-8 md:flex-row">
            <div className="mx-auto w-[220px] shrink-0 overflow-hidden rounded-xl border border-background-50/10 bg-background-900 md:mx-0 md:w-[260px]">
              <img
                src={movie.cover}
                alt={`${movie.title}海报`}
                title={`${movie.title} - 神马影院影视资源`}
                className="aspect-[2/3] w-full object-top"
              />
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">{movie.title}</h1>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1 text-sm font-bold text-background-50 dark:text-foreground-950">
                  <i className="ri-star-fill"></i> {movie.rating.toFixed(1)}
                </span>
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="rounded-full border border-background-50/25 px-3 py-1 text-xs text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-background-400">导演</dt>
                  <dd className="text-background-100">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-background-400">地区</dt>
                  <dd className="text-background-100">{movie.region}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-background-400">主演</dt>
                  <dd className="text-background-100">{movie.actors.join(' / ')}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-background-400">{movie.type === 'movie' ? '片长' : '集数'}</dt>
                  <dd className="text-background-100">{movie.duration}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-background-400">上映</dt>
                  <dd className="text-background-100">{movie.releaseDate}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-background-400">分类</dt>
                  <dd>
                    <Link to={`/category/${movie.type}`} className="text-accent-400 transition-colors hover:text-accent-300">
                      {movie.type === 'movie' ? '电影频道' : '电视剧频道'}
                    </Link>
                  </dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md px-6 py-3 text-sm font-semibold transition-colors ${
                    favorited
                      ? 'bg-secondary-500 text-foreground-950'
                      : 'bg-accent-500 text-background-50 hover:bg-accent-600 dark:text-foreground-950'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-share-forward-line text-lg"></i> 分享
                </button>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-background-400">
                <i className="ri-information-line"></i> 收藏数据仅保存在本地浏览器，不会上传服务器。
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div className="space-y-8">
            <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-file-text-line text-accent-500"></i> 剧情简介
              </h2>
              <p className="text-sm leading-7 text-foreground-600">{movie.synopsis}</p>
              <p className="mt-3 text-sm leading-7 text-foreground-500">
                《{movie.title}》是一部{movie.year}年的{movie.region}{movie.genres.join('、')}作品，由{movie.director}执导，
                {movie.actors.join('、')}等主演。影片自{movie.releaseDate}上线以来获得广泛关注，目前累计播放量已超过
                {(movie.views / 10000).toFixed(0)}万，是{movie.genres[0]}题材中值得一看的佳作。
              </p>
            </section>

            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-chat-quote-line text-accent-500"></i> 影视资讯与剧评
              </h2>
              <ReviewList reviews={movie.reviews} />
            </section>
          </div>

          <aside className="space-y-5">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-bar-chart-line text-primary-600"></i> 热度数据
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-foreground-500"><i className="ri-play-circle-line"></i> 播放量</span>
                  <span className="font-bold text-foreground-900">{(movie.views / 10000).toFixed(0)}万</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-foreground-500"><i className="ri-heart-3-line"></i> 收藏数</span>
                  <span className="font-bold text-foreground-900">{(movie.favorites / 10000).toFixed(1)}万</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-foreground-500"><i className="ri-star-line"></i> 综合评分</span>
                  <span className="font-bold text-secondary-600">{movie.rating.toFixed(1)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-share-line text-primary-600"></i> 分享给好友
              </h3>
              <div className="flex gap-2">
                {['ri-wechat-line', 'ri-weibo-line', 'ri-qq-line', 'ri-link'].map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={handleShare}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-accent-500 hover:text-background-50"
                    aria-label="分享"
                  >
                    <i className={`${icon} text-lg`}></i>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-10 md:mt-12">
          <RelatedMovies current={movie} />
        </div>
      </div>
    </div>
  );
}